import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../store';
import type { RootState } from "../rootReducer";
import { Auth0Client } from '@auth0/auth0-spa-js';
import { combinePathWithDomain } from '../../utils/combinePathWithDomain';

let auth0App: Auth0Client | null = null;

const domain = process.env.REACT_APP_AUTH0_DOMAIN || "";
const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID || "";
const audience = "https://api.brisby.com"; // Hardcode the correct audience

// Function to initialize Auth0 App client
const initAuth0App = async () => {
  console.log('Auth0 App Config:', { domain, clientId, audience });
  if (!auth0App) {
    auth0App = new Auth0Client({
      domain,
      clientId,
      authorizationParams: {
        redirect_uri: `${window.location.origin}/authcallback`,
        audience: audience,
        scope: 'openid profile email read:organizations read:organization_members'
      },
      cacheLocation: 'localstorage'
    });
  }
  return auth0App;
};

// Function to decode JWT token
const decodeToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  token: string | null; 
  role: string | null;
  currentUser: any | null;
  organizationId: string | null;
  roles: string[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
    },
    logoutSuccess(state) {
      state.user = null;
      state.isAuthenticated = false;
    },
    logoutFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    setDisplayName: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user.displayName = action.payload;
      }
    },
  },
});

export const { setUser, clearUser, setDisplayName } = authSlice.actions;

export const loginUser = (): AppThunk => async (dispatch) => {
  try {
    const auth0Client = await initAuth0App();
    await auth0Client.loginWithRedirect();
  } catch (error) {
    console.error('Error logging in:', error);
  }
};

export const resetPassword = (email?: string): AppThunk => async () => {
  try {
    const auth0Client = await initAuth0App();
    await auth0Client.loginWithRedirect({
      authorizationParams: {
        screen_hint: 'reset_password',
        ...(email && { login_hint: email }),
      },
    });
  } catch (error) {
    console.error('Error initiating password reset:', error);
    throw error;
  }
};

export const changePassword = (): AppThunk => async () => {
  try {
    const auth0Client = await initAuth0App();
    await auth0Client.loginWithRedirect({
      authorizationParams: {
        screen_hint: 'change_password',
      },
    });
  } catch (error) {
    console.error('Error initiating password change:', error);
    throw error;
  }
};

// Function to get token with specific audience
const getTokenWithAudience = async (audience: string) => {
  const auth0Client = await initAuth0App();
  try {
    console.log('Getting token with audience:', audience);
    const token = await auth0Client.getTokenSilently({
      authorizationParams: {
        audience: audience,
        scope: 'openid profile email read:organizations read:organization_members',
        response_type: 'token id_token',
        response_mode: 'form_post'
      },
      detailedResponse: true,
      timeoutInSeconds: 10 // Add timeout to prevent hanging
    });

    if (!token?.access_token) {
      throw new Error('No access token received');
    }

    // Verify token is not expired
    const decodedToken = decodeToken(token.access_token);
    if (!decodedToken || !decodedToken.exp) {
      throw new Error('Invalid token format');
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedToken.exp < currentTime) {
      throw new Error('Token has expired');
    }

    console.log('Token received and validated');
    return token.access_token;
  } catch (error) {
    console.error('Error getting token:', error);
    // If token refresh fails, we should clear the user state
    if (error instanceof Error && error.message.includes('login_required')) {
      throw new Error('Authentication required');
    }
    throw error;
  }
};

const fetchAuthState = async (dispatch: any) => {
  let retryCount = 0;
  const maxRetries = 2;

  while (retryCount <= maxRetries) {
    try {
      const auth0App = await initAuth0App();

      // Get token for API access
      console.log('Fetching auth state...');
      const apiToken = await getTokenWithAudience(audience);
      
      if (!apiToken) {
        throw new Error('No API token available');
      }

      // Decode and log the token contents
      const decodedToken = decodeToken(apiToken);
      if (!decodedToken) {
        throw new Error('Failed to decode token');
      }
      
      console.log('Decoded JWT token:', decodedToken);
      console.log('Token scopes:', decodedToken.scope);
      console.log('Token audience:', decodedToken.aud);
      
      const user = await auth0App.getUser();
      console.log('Raw Auth0 user object:', user);

      if (!user?.sub) {
        throw new Error('User not authenticated');
      }

      // Get organization ID from user metadata
      const organizationId = user.org_id;
      console.log('Organization ID:', organizationId);
      
      if (!organizationId) {
        throw new Error('No organization ID found');
      }

      // Fetch user roles with retry logic
      let rolesResponse;
      try {
        rolesResponse = await fetch(
          combinePathWithDomain('/users/roles'),
          {
            headers: {
              Authorization: `Bearer ${apiToken}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
          }
        );
      } catch (error) {
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying roles fetch (attempt ${retryCount})`);
          continue;
        }
        throw error;
      }

      if (!rolesResponse.ok) {
        if (rolesResponse.status === 401 && retryCount < maxRetries) {
          retryCount++;
          console.log(`Token might be expired, retrying (attempt ${retryCount})`);
          continue;
        }
        console.error('Roles response error:', rolesResponse.status, rolesResponse.statusText);
        const errorText = await rolesResponse.text();
        console.error('Error details:', errorText);
        throw new Error(`Failed to fetch roles: ${rolesResponse.status} ${rolesResponse.statusText}`);
      }

      const roles = await rolesResponse.json();
      console.log('Roles response:', roles);

      // Determine role level based on roles
      const isSuperAdmin = roles.includes('super_admin');
      const isPartnerAdmin = roles.includes('partner_admin');
      const isCustomerAdmin = roles.includes('customer_admin');
      const role = isSuperAdmin ? 'super_admin' : isPartnerAdmin ? 'partner_admin' : isCustomerAdmin ? 'customer_admin' : 'user';
      
      dispatch(setUser({
        uid: user.sub,
        email: user.email || null,
        displayName: user.name || null,
        token: apiToken,
        role,
        currentUser: user,
        organizationId: organizationId,
        roles: roles,
      }));
      
      return; // Successfully completed, exit the function
    } catch (error) {
      console.error(`Error fetching auth state (attempt ${retryCount + 1}):`, error);
      
      if (error instanceof Error && 
          (error.message.includes('Authentication required') || 
           error.message.includes('login_required'))) {
        dispatch(clearUser());
        return;
      }
      
      if (retryCount < maxRetries) {
        retryCount++;
        console.log(`Retrying auth state fetch (attempt ${retryCount})`);
        continue;
      }
      
      dispatch(clearUser());
      return;
    }
  }
};

export const logout = (): AppThunk => async (dispatch) => {
  try {
    const auth0Client = await initAuth0App();
    await auth0Client.logout();
    dispatch(clearUser());
  } catch (error) {
    console.error('Error logging out:', error);
  }
};

export const checkAuthState = (): AppThunk => async (dispatch) => {
  try {
    const auth0Client = await initAuth0App();
    
    // Check if we're on the callback URL and handle it first
    const query = window.location.search;
    if (query.includes('code=') || query.includes('error=')) {
      // We're on the callback URL, let handleAuthCallback handle it
      // This will be called by the AuthCallback component
      return;
    }
    
    // Otherwise, check auth state normally
    await fetchAuthState(dispatch);
  } catch (error) {
    console.error('Error checking auth state:', error);
    dispatch(clearUser());
  }
};

export const handleAuthCallback = (): AppThunk => async (dispatch) => {
  try {
    const auth0Client = await initAuth0App();
    
    // Check if we're handling a redirect callback from Auth0
    const isAuthenticated = await auth0Client.isAuthenticated();
    
    if (!isAuthenticated) {
      // Check if there's a callback in the URL
      const query = window.location.search;
      if (query.includes('code=') || query.includes('error=')) {
        // Handle the redirect callback
        await auth0Client.handleRedirectCallback();
      }
    }
    
    // Now fetch the auth state
    await fetchAuthState(dispatch);
  } catch (error) {
    console.error('Error handling auth callback:', error);
    dispatch(clearUser());
  }
};

export const selectJwtToken = (state: RootState) => state.auth.user?.token;

export const updateDisplayName = (displayName: string | null): AppThunk => (dispatch) => {
  dispatch(setDisplayName(displayName));
};


export const completeLogout = async (): Promise<void> => {
  try {
    const auth0Client = await initAuth0App();
    
    // Clear all Auth0 data from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('auth0') || 
        key.includes('auth0') || 
        key.includes('auth') ||
        key.includes('token')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Clear session storage
    sessionStorage.clear();

    // Logout from Auth0
    await auth0Client.logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  } catch (error) {
    console.error('Error during complete logout:', error);
  }
};

export default authSlice.reducer;